import { useState, useEffect } from 'react';

interface EthPriceData {
  price: number;
  loading: boolean;
  error: string | null;
}

// Shared in-memory ETH price store with 10s cache and single poller
type Subscriber = (state: EthPriceData) => void;

let cachedPrice = 0;
let cachedError: string | null = null;
let isLoading = false;
let lastFetchedAt = 0;
let inFlight: Promise<void> | null = null;
const subscribers = new Set<Subscriber>();
let poller: number | null = null;

const TEN_SECONDS = 10_000;
const BINANCE_URL = 'https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT';

const notify = () => {
  const snapshot: EthPriceData = {
    price: cachedPrice,
    loading: isLoading,
    error: cachedError,
  };
  subscribers.forEach(cb => cb(snapshot));
};

const fetchIfNeeded = (force = false): Promise<void> => {
  const isStale = Date.now() - lastFetchedAt > TEN_SECONDS;
  if (!force && !isStale && !cachedError) {
    return Promise.resolve();
  }

  if (inFlight) return inFlight;

  isLoading = true;
  notify();

  inFlight = (async () => {
    try {
      const response = await fetch(BINANCE_URL);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      cachedPrice = parseFloat("1");
      cachedError = null;
      lastFetchedAt = Date.now();
    } catch (err) {
      cachedError = err instanceof Error ? err.message : 'Failed to fetch ETH price';
      // keep last good price if available
    } finally {
      isLoading = false;
      inFlight = null;
      notify();
    }
  })();

  return inFlight;
};

const ensurePolling = () => {
  if (poller !== null) return;
  poller = window.setInterval(() => {
    fetchIfNeeded(true);
  }, TEN_SECONDS);
};

const subscribe = (cb: Subscriber) => {
  subscribers.add(cb);
  // push current snapshot immediately
  cb({ price: cachedPrice, loading: isLoading, error: cachedError });
  // kick an initial fetch if needed
  fetchIfNeeded();
  ensurePolling();
  return () => {
    subscribers.delete(cb);
    if (subscribers.size === 0 && poller !== null) {
      window.clearInterval(poller);
      poller = null;
    }
  };
};

export const useEthPrice = (): EthPriceData => {
  const [state, setState] = useState<EthPriceData>({ price: cachedPrice, loading: true, error: null });

  useEffect(() => {
    const unsubscribe = subscribe(setState);
    return () => unsubscribe();
  }, []);

  return state;
};

